<template>
	<div v-if="table">
		<!-- En-tête : titre (contexte fiche jument) + bouton ajout -->
		<div class="d-flex align-items-center my-2" v-if="mare_nom || from !== 'list'">
			<h2 v-if="mare_nom" class="mb-0 mr-auto text-truncate">
				<font-awesome-icon :icon="['fal', 'mars']" class="icon-groomy-red" /> {{ $t('monte.contrats_saillies_de') }} {{ mare_nom }}
			</h2>
			<div v-else class="mr-auto"></div>
			<b-form-select
				v-if="mare_id && season_options_select.length > 1"
				v-model="season_selected_val"
				:options="season_options_select"
				size="sm"
				style="width:auto"
				class="mr-2"
			/>
			<b-button v-if="from !== 'list'" @click="handleAddContract" variant="primary" size="sm" class="ml-2 flex-shrink-0">
				{{ $t('action.ajouter_contract') }}
				<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse />
				<font-awesome-icon v-else :icon="['fal', 'plus']"/>
			</b-button>
		</div>

		<!-- Filtres : saison (étalon/autres) + client signataire (hors contexte page /monte/contrats) -->
		<div v-if="contracts.length > 0 && from !== 'list'" class="row mb-3">
			<div v-if="!mare_id" class="col-12 col-md-auto mb-2 mb-md-0">
				<e-select
					id="season_filter"
					track-by="value"
					label="label"
					:options="season_options"
					:searchable="false"
					:show-labels="false"
					:allow-empty="false"
					v-model="season_selected"
				/>
			</div>
			<div v-if="uniqueTiersCount > 1" class="col-12 col-md-auto">
				<e-select
					id="tiers_filter"
					track-by="tiers_id"
					label="tiers_rs"
					:options="all_tiers_options"
					:searchable="true"
					:show-labels="false"
					:placeholder="$t('invoice.tous_clients')"
					v-model="tiers_selected"
				>
					<template slot="option" slot-scope="{ option }">{{ option.tiers_rs }}</template>
					<template slot="singleLabel" slot-scope="{ option }">{{ option.tiers_rs }}</template>
					<template slot="noOptions">{{ $t('global.list_empty') }}</template>
				</e-select>
			</div>
		</div>

		<div v-if="table_busy">
			<LoadingSpinner />
		</div>

		<div class="mt-2" v-else>
			<div v-if="contracts.length == 0" class="col-12">
				<EmptyState :label="$t('global.empty_state_default')">
					<template #action>
						<b-button @click="handleAddContract" variant="primary" size="sm">
							{{ $t('action.ajouter_contract') }}
							<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse />
							<font-awesome-icon v-else :icon="['fal', 'plus']"/>
						</b-button>
					</template>
				</EmptyState>
			</div>

			<!-- En-tête du tableau (desktop uniquement, masqué si aucun résultat) -->
			<div v-if="contracts.length > 0" class="d-none d-lg-flex row font-weight-bold border-bottom pb-2 mb-1">
				<div class="col-lg-2">{{ $t('monte.saison') }}</div>
				<div class="col-lg-2">{{ $t('monte.numero_contrat') }}</div>
				<div class="col-lg-2">{{ $t('monte.jument') }}</div>
				<div class="col-lg-2">{{ $t('monte.stallion') }}</div>
				<div class="col-lg-2">{{ $t('monte.statut') }}</div>
				<div class="col-lg-2"></div>
			</div>

			<div v-for="contract in contracts_sorted" :key="contract.contract_id" class="border-bottom">
				<!-- Desktop : ligne tableau horizontale -->
				<div class="d-none d-lg-flex row align-items-center py-2">
					<div class="col-lg-2 text-truncate">{{ contract.season_label }}</div>
					<div class="col-lg-2 text-truncate">{{ contract.contract_num }}</div>
					<div class="col-lg-2 text-truncate">{{ contract.mare_nom }}</div>
					<div class="col-lg-2 text-truncate">{{ contract.stallion_nom }}</div>
					<div class="col-lg-2 text-truncate">{{ contract.avenant_status }}</div>
					<div class="col-lg-2 text-right">
						<b-button v-if="contract.mare_id" variant="secondary" class="w-100" @click="goToContract(contract.contract_id, contract.mare_id)">{{ $t('global.consulter') }}</b-button>
					</div>
				</div>
				<!-- Mobile : carte cliquable (uniquement si jument connue) -->
				<div
					class="d-flex d-lg-none align-items-center py-2"
					:style="contract.mare_id ? 'cursor:pointer' : ''"
					@click="contract.mare_id && goToContract(contract.contract_id, contract.mare_id)"
				>
					<div class="flex-fill min-w-0 overflow-hidden">
						<div class="d-flex align-items-baseline">
							<strong class="mr-2 text-truncate">{{ contract.contract_num }}</strong>
							<small class="text-muted text-truncate">{{ contract.season_label }}</small>
						</div>
						<div class="text-truncate">
							{{ contract.mare_nom }}<span v-if="contract.stallion_nom" class="text-muted"> × {{ contract.stallion_nom }}</span>
						</div>
						<small class="text-muted">{{ contract.avenant_status }}</small>
					</div>
					<font-awesome-icon v-if="contract.mare_id" :icon="['fal', 'chevron-right']" class="text-muted ml-2 flex-shrink-0" />
				</div>
			</div>
		</div>

		<TiersModal ref="tiers_modal" @submit="tiersInfosChecked" />
	</div>
	<div v-else>
		<ContractShutter :contract_id="contract_id" :mare_id="mare_id"></ContractShutter>
	</div>
</template>

<script type="text/javascript">
import ContractMixin from "@/mixins/Contract.js"
import TiersMixin from "@/mixins/Tiers.js"
import Vue from 'vue'

/**
 * Liste des contrats de saillie, utilisée dans deux contextes :
 *  - Page autonome /monte/contrats  → via ContractList.vue (from="list", bouton dans son HeaderTab)
 *  - Onglet fiche jument            → via ContractMare.vue  (mare_id + mare_nom fournis en props)
 */
export default {
	name: "ContractListV2",
	mixins: [ContractMixin, TiersMixin],
	props: {
		mare_id:         { default: null },
		stallion_id:     { default: null },
		mare_nom:        { default: null },
		/** 'list' quand appelé depuis la page /monte/contrats (filtres et bouton ajout dans le HeaderTab du parent) */
		from: { type: String, default: null },
		/** Filtres contrôlés par le parent quand from === 'list' */
		season_filter:   { default: null },
		tiers_filter:    { default: null },
	},
	data () {
		return {
			contracts: [],
			/** true : affiche la liste ; false : bascule sur ContractShutter immédiatement après création */
			table: true,
			table_busy: false,
			/** active le spinner sur le bouton ajout pendant la vérification de la période contractuelle */
			processing: false,
			all_tiers: [],
			tiers_selected: null,
			/** Saison active dans le filtre, initialisée à la plus récente après chargement */
			season_selected: null,
		}
	},
	computed: {
		uniqueTiersCount() {
			return new Set(this.contracts.map(c => c.tiers_id)).size
		},
		/**
		 * Options du filtre saison : saisons distinctes triées desc.
		 * Une option "Toutes les saisons" est ajoutée quand il y a plusieurs saisons.
		 */
		season_options() {
			// Reconstruit un map label → season_start pour trier sur la vraie date
			const labelToStart = {}
			this.contracts.forEach(c => {
				if (c.season_label && c.season_start) labelToStart[c.season_label] = c.season_start
			})
			const seasons = [...new Set(this.contracts.map(c => c.season_label))]
				.filter(Boolean)
				.sort((a, b) => {
					const sa = labelToStart[a] || a
					const sb = labelToStart[b] || b
					return sb > sa ? 1 : sb < sa ? -1 : 0
				})
			const opts = seasons.map(s => ({ value: s, label: s }))
			if (opts.length > 1) {
				opts.push({ value: null, label: this.getTrad('monte.toutes_les_saisons') })
			}
			return opts
		},
		/**
		 * Options du filtre client : liste complète précédée d'une entrée "Tous" (tiers_id null).
		 */
		all_tiers_options() {
			const all = { tiers_id: null, tiers_rs: this.getTrad('invoice.tous_clients') }
			return [all, ...(this.all_tiers || [])]
		},
		/** Options converties en { value, text } pour b-form-select (fiche jument) */
		season_options_select() {
			return this.season_options.map(o => ({ value: o.value, text: o.label }))
		},
		/** Bridge entre b-form-select (valeur primitive) et season_selected (objet e-select) */
		season_selected_val: {
			get() {
				return this.season_selected ? this.season_selected.value : null
			},
			set(val) {
				this.season_selected = this.season_options.find(o => o.value === val) || null
			}
		},
		/**
		 * Liste des contrats filtrée par saison et tiers (si sélectionnés), triée :
		 * - Saison décroissante
		 * - À saison égale, numéro de contrat croissant (ordre alphanumérique)
		 * Quand from === 'list', utilise les filtres passés en props par le parent (ContractList).
		 */
		contracts_sorted() {
			// Quand from === 'list', les filtres viennent du parent sous forme de valeurs primitives
			const activeSeason = this.from === 'list'
				? this.season_filter
				: (this.season_selected ? this.season_selected.value : null)
			const activeTiers = this.from === 'list'
				? this.tiers_filter
				: (this.tiers_selected ? this.tiers_selected.tiers_id : null)
			let list = this.contracts
			if (activeSeason !== null && activeSeason !== undefined) {
				list = list.filter(c => c.season_label === activeSeason)
			}
			if (activeTiers !== null && activeTiers !== undefined) {
				list = list.filter(c => c.tiers_id === activeTiers)
			}
			return list.slice().sort((a, b) => {
				const s = parseInt(b.season_label) - parseInt(a.season_label)
				if (s !== 0) return s
				const da = a.contract_created ? new Date(a.contract_created) : new Date(0)
				const db = b.contract_created ? new Date(b.contract_created) : new Date(0)
				return db - da
			})
		}
	},
	mounted() {
		this.init_component()
	},
	watch: {
		mare_id(newVal, oldVal) {
			if (newVal !== oldVal) {
				this.init_component()
			}
		}
	},
	methods: {
		async init_component() {
			this.table_busy = true
			
			let contracts = []
			if(this.mare_id) {
				contracts = await this.getContractByTiersAndHorse(this.mare_id)
			}
			else if(this.stallion_id) {
				contracts = await this.getContractByTiersAndHorse(this.stallion_id)
			}
			else {
				contracts = await this.getContractByTiers()
			}
				this.contracts = this.contracts_formatted(contracts)
			
			this.all_tiers = this.getConfig('all_tiers')

			// Fiche jument : "Toutes les saisons" par défaut ; autres contextes : saison la plus récente
			if (this.mare_id) {
				this.season_selected = this.season_options.find(o => o.value === null) || this.season_options[0] || null
			} else if (this.season_options.length > 0) {
				this.season_selected = this.season_options[0]
			}

			// Informer le parent (ContractList) des options disponibles pour qu'il les affiche dans son HeaderTab
			if (this.from === 'list') {
				this.$emit('filters-ready', {
					season_options:    this.season_options,
					all_tiers_options: this.all_tiers_options,
					season_selected:   this.season_options[0] || null,
					uniqueTiersCount:  this.uniqueTiersCount,
				})
			}

			this.table_busy = false
		},
		contracts_formatted(contracts) {
			return contracts.map(contract => ({
				contract_id:     contract.contract_id,
				contract_num:   (contract.contract_num == null) ? Vue.i18n.translate('monte.contrat_brouillon_non_numerote') : contract.contract_num,
				contract_created: contract.contract_created || null,
				tiers_id:       contract.tiers_id,
				mare_nom:       contract.mare_nom,
				mare_id:        contract.mare_id,
				stallion_nom:   contract.stallion_nom,
				season_label:   contract.season_label,
				season_start:   contract.season ? contract.season.season_start : null,
				avenant_status: this.getTrad(contract.avenant_status),
			}))
		},
		/**
		 * Gère le clic sur "Ajouter un contrat".
		 * Si une jument est déjà sélectionnée, navigue directement vers ContractNew.
		 * Sinon, délègue à checkDateContract pour vérifier la période d'ouverture.
		 */
		handleAddContract() {
			if (this.mare_id) {
				this.$router.push({ name: 'ContractNew', params: { mare_id: this.mare_id } })
			} else {
				this.checkDateContract()
			}
		},
		/**
		 * Vérifie si la période de création de contrats est ouverte via la politique
		 * de confidentialité, puis proceed vers la saisie des infos tiers ou affiche
		 * un toast d'erreur si la période est fermée.
		 * @returns {Promise<void>}
		 */
		async checkDateContract() {
			this.processing = true
			const privacy = await this.contractPrivacyPolicy()
			const { closed, message } = this.isContractCreationClosed(privacy)
			if (closed) {
				this.failureToast(message, true)
				this.processing = false
				return
			}
			this.checkInfosTiers()
		},
		async checkInfosTiers() {
			let logged_tiers = await this.loadLoggedTiers()
			this.$refs.tiers_modal.openModal(logged_tiers)
		},
		async tiersInfosChecked(infos_tiers_filled) {
			if(infos_tiers_filled) {
				const contract = await this.addContract()
				this.contract_id = contract.contract_id
				this.table = false
			}
		},
		/**
		 * Navigue vers la fiche contrat dans l'onglet de la jument.
		 * @param {number} contract_id
		 * @param {number} mare_id - horse_id de la jument (obligatoire : les contrats sans jument n'ont pas de bouton)
		 */
		goToContract(contract_id, mare_id) {
			const horse_id = mare_id || this.mare_id || this.stallion_id
			this.$router.push({ name: 'ContractMareFiche', params: { horse_id, contract_id } })
		},
	},
	components: {
		ContractShutter : () => import('@/components/Contract/ContractShutter'),
		LoadingSpinner : () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
		TiersModal: () => import('@/components/Contract/InformationsTiersForm'),
		EmptyState: () => import('@/components/EmptyState')
	},
}
</script>
